/* =========================================================
   RIZALE — theme.js (no dependencies)
   ========================================================= */
(() => {
  'use strict';

  const R = window.Rizale || {};
  const $ = (s, c = document) => c.querySelector(s);
  const $$ = (s, c = document) => Array.from(c.querySelectorAll(s));
  const on = (el, ev, fn, opts) => el && el.addEventListener(ev, fn, opts);
  const debounce = (fn, ms = 250) => { let t; return (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), ms); }; };
  const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');

  /* ---------------- Toast ---------------- */
  const toast = (msg) => {
    const el = $('[data-toast]');
    if (!el) return;
    el.textContent = msg;
    el.classList.add('is-visible');
    clearTimeout(el._t);
    el._t = setTimeout(() => el.classList.remove('is-visible'), 2600);
  };

  /* ---------------- Overlay / drawers ---------------- */
  const overlay = $('[data-overlay]');
  let openPanel = null;
  let lastFocus = null;

  const openPanelEl = (panel) => {
    if (!panel) return;
    if (openPanel && openPanel !== panel) closePanel(false);
    lastFocus = document.activeElement;
    openPanel = panel;
    overlay.hidden = false;
    requestAnimationFrame(() => {
      overlay.classList.add('is-visible');
      panel.classList.add('is-open');
    });
    panel.setAttribute('aria-hidden', 'false');
    document.body.classList.add('is-locked');
    const focusable = panel.querySelector('input, button, a[href]');
    setTimeout(() => focusable && focusable.focus({ preventScroll: true }), 120);
  };

  const closePanel = (restore = true) => {
    if (!openPanel) return;
    openPanel.classList.remove('is-open');
    openPanel.setAttribute('aria-hidden', 'true');
    overlay.classList.remove('is-visible');
    setTimeout(() => { if (!openPanel) overlay.hidden = true; }, 450);
    document.body.classList.remove('is-locked');
    openPanel = null;
    if (restore && lastFocus) lastFocus.focus({ preventScroll: true });
  };

  on(overlay, 'click', () => closePanel());
  on(document, 'keydown', (e) => {
    if (e.key === 'Escape') {
      closePanel();
      $$('.modal.is-open').forEach(closeModal);
      closeLightbox();
    }
  });
  on(document, 'click', (e) => {
    const opener = e.target.closest('[data-open]');
    if (opener) {
      const target = document.getElementById(opener.dataset.open);
      if (target) {
        if (opener.dataset.open === 'CartDrawer' && R.settings && R.settings.cartType === 'page') return;
        e.preventDefault();
        if (target.classList.contains('modal')) openModal(target);
        else openPanelEl(target);
      }
    }
    if (e.target.closest('[data-close]')) {
      e.preventDefault();
      const modal = e.target.closest('.modal');
      if (modal) closeModal(modal); else closePanel();
    }
  });

  /* ---------------- Modal ---------------- */
  function openModal(m) { m.classList.add('is-open'); m.setAttribute('aria-hidden', 'false'); document.body.classList.add('is-locked'); }
  function closeModal(m) { m.classList.remove('is-open'); m.setAttribute('aria-hidden', 'true'); if (!openPanel) document.body.classList.remove('is-locked'); }
  $$('.modal__backdrop').forEach((b) => on(b, 'click', () => closeModal(b.closest('.modal'))));

  /* ---------------- Header ---------------- */
  const headerWrap = $('[data-header]');
  if (headerWrap) {
    let lastY = window.scrollY;
    const sticky = headerWrap.dataset.sticky || 'always';
    const setH = () => document.documentElement.style.setProperty('--header-height', headerWrap.offsetHeight + 'px');
    setH();
    on(window, 'resize', debounce(setH, 150));
    const stickyEl = headerWrap.closest('.shopify-section') || headerWrap;
    const onScroll = () => {
      const y = window.scrollY;
      headerWrap.classList.toggle('is-scrolled', y > 10);
      if (sticky === 'on-scroll-up' && !openPanel) {
        stickyEl.classList.toggle('is-hidden', y > lastY && y > 300);
      }
      lastY = y;
    };
    on(window, 'scroll', onScroll, { passive: true });
    onScroll();
  }

  /* ---------------- Reveal animations ---------------- */
  const initReveal = (root = document) => {
    $$('[data-split]', root).forEach((el) => {
      if (el.dataset.splitDone) return;
      el.dataset.splitDone = '1';
      const lines = el.innerHTML.split(/<br\s*\/?>/i);
      el.innerHTML = lines.map((l, i) => `<span class="split-line"><span style="--delay:${(i * 0.12).toFixed(2)}s">${l}</span></span>`).join('');
      if (!el.hasAttribute('data-reveal')) el.setAttribute('data-reveal', 'fade');
    });
    const els = $$('[data-reveal]:not(.is-revealed)', root);
    if (!document.body.classList.contains('has-animations') || !('IntersectionObserver' in window)) {
      els.forEach((el) => el.classList.add('is-revealed'));
      return;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((en) => {
        if (en.isIntersecting) { en.target.classList.add('is-revealed'); io.unobserve(en.target); }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });
    els.forEach((el) => io.observe(el));
  };
  initReveal();

  /* ---------------- Money ---------------- */
  const cartPost = (url, body) => fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body)
  }).then(async (r) => {
    const data = await r.json();
    if (!r.ok || data.status) throw new Error(data.description || data.message || R.strings.error);
    return data;
  });

  /* ---------------- Cart ---------------- */
  const CART_SECTION = 'cart-drawer';

  const renderCart = (sections) => {
    if (!sections) return;
    const html = sections[CART_SECTION];
    if (html) {
      const doc = parseHTML(html);
      const fresh = $('[data-cart-inner]', doc);
      const cur = $('[data-cart-inner]');
      if (fresh && cur) cur.replaceWith(fresh);
      const count = $('[data-cart-count-source]', doc);
      if (count) {
        $$('[data-cart-count]').forEach((c) => { c.textContent = count.dataset.cartCountSource; c.dataset.count = count.dataset.cartCountSource; });
      }
    }
    if (sections['main-cart'] && $('[data-main-cart]')) {
      const doc = parseHTML(sections['main-cart']);
      const fresh = $('[data-main-cart]', doc);
      if (fresh) $('[data-main-cart]').replaceWith(fresh);
    }
  };

  const sectionsToRender = () => {
    const s = [CART_SECTION];
    if ($('[data-main-cart]')) s.push($('[data-main-cart]').dataset.sectionId || 'main-cart');
    return s;
  };

  const addToCart = (items, btn) => {
    if (btn) btn.classList.add('is-loading');
    return cartPost(R.routes.cartAdd + '.js', { items, sections: sectionsToRender(), sections_url: window.location.pathname })
      .then((res) => {
        renderCart(normalizeSections(res.sections));
        if (R.settings.cartType === 'page') {
          toast(R.strings.added);
        } else {
          openPanelEl($('#CartDrawer'));
        }
        document.dispatchEvent(new CustomEvent('rizale:cart:added', { detail: res }));
      })
      .catch((err) => toast(err.message))
      .finally(() => btn && btn.classList.remove('is-loading'));
  };

  // The main-cart section id is dynamic on cart page; map back to "main-cart" key for renderCart.
  const normalizeSections = (sections) => {
    if (!sections) return sections;
    const mc = $('[data-main-cart]');
    if (mc && mc.dataset.sectionId && sections[mc.dataset.sectionId]) sections['main-cart'] = sections[mc.dataset.sectionId];
    return sections;
  };

  const changeLine = (line, quantity, itemEl) => {
    if (itemEl) itemEl.classList.add('is-loading');
    return cartPost(R.routes.cartChange + '.js', { line, quantity, sections: sectionsToRender(), sections_url: window.location.pathname })
      .then((res) => renderCart(normalizeSections(res.sections)))
      .catch((err) => { toast(err.message); if (itemEl) itemEl.classList.remove('is-loading'); });
  };

  on(document, 'click', (e) => {
    const rm = e.target.closest('[data-cart-remove]');
    if (rm) {
      e.preventDefault();
      changeLine(Number(rm.dataset.line), 0, rm.closest('.cart-item'));
      return;
    }
    const q = e.target.closest('[data-qty-btn]');
    if (q) {
      e.preventDefault();
      const wrap = q.closest('.qty');
      const input = $('input', wrap);
      const step = q.dataset.qtyBtn === 'plus' ? 1 : -1;
      const min = Number(input.min || 0);
      const v = Math.max(min, (Number(input.value) || 0) + step);
      input.value = v;
      input.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }
    const up = e.target.closest('[data-upsell-add]');
    if (up) {
      e.preventDefault();
      addToCart([{ id: Number(up.dataset.upsellAdd), quantity: 1 }], up);
    }
    const quick = e.target.closest('[data-quick-add]');
    if (quick) {
      e.preventDefault();
      addToCart([{ id: Number(quick.dataset.quickAdd), quantity: 1 }], quick.classList.contains('btn') ? quick : null);
    }
  });

  on(document, 'change', debounce((e) => {
    const input = e.target.closest('[data-cart-qty]');
    if (input) changeLine(Number(input.dataset.line), Number(input.value), input.closest('.cart-item'));
  }, 350));

  on(document, 'input', debounce((e) => {
    const note = e.target.closest('[data-cart-note]');
    if (note) cartPost('/cart/update.js', { note: note.value }).catch(() => {});
  }, 600));

  /* ---------------- Product card swatches ---------------- */
  on(document, 'click', (e) => {
    const sw = e.target.closest('[data-card-swatch]');
    if (!sw) return;
    e.preventDefault();
    const card = sw.closest('.card');
    $$('[data-card-swatch]', card).forEach((s) => s.classList.toggle('is-active', s === sw));
    const img = $('.card__img-1', card);
    if (img && sw.dataset.image) {
      img.srcset = '';
      img.src = sw.dataset.image;
    }
    const link = $('.card__title a', card);
    if (link && sw.dataset.url) link.href = sw.dataset.url;
    $$('[data-quick-group]', card).forEach((g) => { g.hidden = g.dataset.quickGroup !== sw.dataset.value; });
  });

  /* ---------------- Product form ---------------- */
  class ProductForm {
    constructor(root) {
      this.root = root;
      const json = $('[data-product-json]', root);
      if (!json) return;
      this.data = JSON.parse(json.textContent);
      this.form = $('form[data-product-form]', root);
      this.idInput = $('input[name="id"]', this.form);
      this.btn = $('[data-add-btn]', root);
      this.price = $('[data-price]', root);
      this.stock = $('[data-stock]', root);
      on(root, 'change', (e) => { if (e.target.matches('[data-option]')) this.update(e.target); });
      on(this.form, 'submit', (e) => this.submit(e));
      this.update();
      this.initSticky();
    }

    selected() {
      return this.data.options.map((_, i) => {
        const checked = $(`[data-option-index="${i}"]:checked`, this.root) || $(`select[data-option-index="${i}"]`, this.root);
        return checked ? checked.value : null;
      });
    }

    update(changed) {
      const sel = this.selected();
      const variant = this.data.variants.find((v) => v.options.every((o, i) => o === sel[i]));
      this.variant = variant;
      // Selected value labels
      sel.forEach((val, i) => { const l = $(`[data-option-value-label="${i}"]`, this.root); if (l) l.textContent = val; });
      // Availability per option value given the other selections
      this.data.options.forEach((_, i) => {
        $$(`input[data-option-index="${i}"]`, this.root).forEach((inp) => {
          const test = sel.slice(); test[i] = inp.value;
          const match = this.data.variants.find((v) => v.options.every((o, k) => o === test[k]));
          inp.classList.toggle('is-unavailable', !match || !match.available);
        });
      });
      if (!variant) {
        this.setBtn(false, R.strings.unavailable);
        return;
      }
      this.idInput.value = variant.id;
      if (this.price) {
        this.price.innerHTML = variant.compare_at_price > variant.price
          ? `<span class="price__current">${variant.price_formatted}</span><s class="price__compare">${variant.compare_formatted}</s>${variant.save_label ? `<span class="badge badge--sale">${variant.save_label}</span>` : ''}`
          : `<span class="price__current">${variant.price_formatted}</span>`;
        this.price.classList.toggle('price--sale', variant.compare_at_price > variant.price);
      }
      this.setBtn(variant.available, variant.available ? R.strings.addToCart : R.strings.soldOut);
      if (this.stock) {
        const low = variant.available && variant.inventory_management && variant.inventory_quantity > 0 && variant.inventory_quantity <= this.data.low_stock;
        this.stock.hidden = !variant.available;
        this.stock.classList.toggle('stock-note--low', !!low);
        this.stock.textContent = low ? this.data.low_stock_text.replace('[qty]', variant.inventory_quantity) : this.data.in_stock_text;
      }
      // Swap featured image if the variant has one
      if (changed && variant.featured_media_id) {
        const m = $(`[data-media-id="${variant.featured_media_id}"]`, this.root);
        if (m) {
          const gallery = m.parentElement;
          if (window.matchMedia('(max-width: 989px)').matches) gallery.scrollTo({ left: m.offsetLeft - 16, behavior: 'smooth' });
          else if (gallery.firstElementChild !== m) { gallery.prepend(m); window.scrollTo({ top: gallery.getBoundingClientRect().top + window.scrollY - 100, behavior: 'smooth' }); }
        }
      }
      if (changed && this.data.update_url !== false) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
      $$('[data-sticky-price]').forEach((p) => { p.textContent = variant.price_plain; });
    }

    setBtn(enabled, label) {
      [this.btn, $('[data-sticky-add]')].forEach((b) => {
        if (!b) return;
        b.disabled = !enabled;
        const span = $('span', b) || b;
        span.textContent = label;
      });
    }

    submit(e) {
      e.preventDefault();
      if (!this.variant || !this.variant.available) return;
      const qty = Number(($('[name="quantity"]', this.form) || {}).value || 1);
      const item = { id: this.variant.id, quantity: qty };
      const fd = new FormData(this.form);
      const props = {};
      for (const [k, v] of fd.entries()) { const m = k.match(/^properties\[(.+)\]$/); if (m && v) props[m[1]] = v; }
      if (Object.keys(props).length) item.properties = props;
      addToCart([item], this.btn);
    }

    initSticky() {
      const bar = $('[data-sticky-atc]');
      if (!bar || !this.btn || !('IntersectionObserver' in window)) return;
      const io = new IntersectionObserver(([en]) => {
        bar.classList.toggle('is-visible', !en.isIntersecting && en.boundingClientRect.top < 0);
      });
      io.observe(this.btn);
      on($('[data-sticky-add]', bar), 'click', (e) => { e.preventDefault(); this.form.requestSubmit ? this.form.requestSubmit() : this.submit(e); });
    }
  }
  $$('[data-product]').forEach((el) => new ProductForm(el));

  /* ---------------- Product recommendations ---------------- */
  const loadRecommendations = (root = document) => {
    $$('[data-recommendations]', root).forEach((el) => {
      if (el.dataset.loaded || $('[data-recommendations-inner]', el)) return;
      el.dataset.loaded = '1';
      fetch(el.dataset.recommendations)
        .then((r) => r.text())
        .then((html) => {
          const inner = $('[data-recommendations-inner]', parseHTML(html));
          if (inner) { el.appendChild(inner); initReveal(el); } else { el.hidden = true; }
        })
        .catch(() => { el.hidden = true; });
    });
  };
  loadRecommendations();

  /* ---------------- Lightbox ---------------- */
  const lightbox = $('[data-lightbox]');
  function closeLightbox() { if (lightbox && lightbox.classList.contains('is-open')) { lightbox.classList.remove('is-open'); document.body.classList.remove('is-locked'); } }
  on(document, 'click', (e) => {
    const m = e.target.closest('[data-zoom]');
    if (!m || !lightbox) return;
    const scroller = $('.lightbox__scroller', lightbox);
    const imgs = $$('[data-zoom]').map((z) => `<img src="${z.dataset.zoom}" alt="" loading="lazy">`);
    scroller.innerHTML = imgs.join('');
    lightbox.classList.add('is-open');
    document.body.classList.add('is-locked');
    const idx = $$('[data-zoom]').indexOf(m);
    const target = scroller.children[idx];
    if (target) target.addEventListener('load', () => target.scrollIntoView(), { once: true });
  });
  if (lightbox) on($('.lightbox__close', lightbox), 'click', closeLightbox);

  /* ---------------- Tabs ---------------- */
  on(document, 'click', (e) => {
    const tab = e.target.closest('[data-tab]');
    if (!tab) return;
    const wrap = tab.closest('[data-tabs]');
    $$('[data-tab]', wrap).forEach((t) => { const a = t === tab; t.classList.toggle('is-active', a); t.setAttribute('aria-selected', a); });
    $$('[data-tab-panel]', wrap).forEach((p) => { p.hidden = p.dataset.tabPanel !== tab.dataset.tab; if (!p.hidden) initReveal(p); });
    const link = $('[data-tab-link]', wrap);
    if (link && tab.dataset.url) link.href = tab.dataset.url;
    const panel = $(`[data-tab-panel="${tab.dataset.tab}"]`, wrap);
    const slider = panel && $('.slider', panel);
    if (slider) $$('[data-slide]', wrap).forEach((b) => { b.dataset.target = slider.id; });
  });

  /* ---------------- Sliders ---------------- */
  on(document, 'click', (e) => {
    const b = e.target.closest('[data-slide]');
    if (!b) return;
    const slider = document.getElementById(b.dataset.target);
    if (!slider) return;
    const item = slider.firstElementChild;
    const step = item ? item.getBoundingClientRect().width + 20 : slider.clientWidth;
    slider.scrollBy({ left: b.dataset.slide === 'next' ? step : -step, behavior: 'smooth' });
  });

  /* ---------------- Hero slideshow ---------------- */
  $$('[data-hero-slider]').forEach((root) => {
    const slides = $$('.hero-slide', root);
    const dots = $$('.hero-dot', root);
    if (slides.length < 2) return;
    const speed = Number(root.dataset.speed || 6) * 1000;
    let i = 0, timer;
    const go = (n) => {
      slides[i].classList.remove('is-active'); dots[i] && dots[i].classList.remove('is-active');
      i = (n + slides.length) % slides.length;
      slides[i].classList.add('is-active'); dots[i] && dots[i].classList.add('is-active');
      initReveal(slides[i]);
    };
    const play = () => { clearInterval(timer); timer = setInterval(() => go(i + 1), speed); };
    dots.forEach((d, n) => on(d, 'click', () => { go(n); play(); }));
    play();
  });

  /* ---------------- Countdown ---------------- */
  $$('[data-countdown]').forEach((el) => {
    const end = new Date(el.dataset.countdown).getTime();
    if (isNaN(end)) { el.hidden = true; return; }
    const pad = (n) => String(n).padStart(2, '0');
    const tick = () => {
      const d = Math.max(0, end - Date.now());
      const parts = { days: Math.floor(d / 864e5), hours: Math.floor(d / 36e5) % 24, minutes: Math.floor(d / 6e4) % 60, seconds: Math.floor(d / 1e3) % 60 };
      Object.entries(parts).forEach(([k, v]) => { const n = $(`[data-cd="${k}"]`, el); if (n) n.textContent = pad(v); });
      if (d <= 0) clearInterval(t);
    };
    const t = setInterval(tick, 1000);
    tick();
  });

  /* ---------------- Category list: swap image on hover ---------------- */
  $$('[data-catlist]').forEach((root) => {
    const imgs = $$('.catlist-img', root);
    const rows = $$('[data-catlist-row]', root);
    const show = (i) => {
      imgs.forEach((im, k) => im.classList.toggle('is-active', k === i));
      rows.forEach((r, k) => r.classList.toggle('is-active', k === i));
    };
    rows.forEach((r) => {
      const i = Number(r.dataset.catlistRow);
      on(r, 'mouseenter', () => show(i));
      on(r, 'focus', () => show(i));
    });
  });

  /* ---------------- Lookbook ---------------- */
  on(document, 'click', (e) => {
    const h = e.target.closest('[data-hotspot]');
    if (!h) return;
    const root = h.closest('[data-lookbook]');
    $$('[data-hotspot]', root).forEach((x) => x.classList.toggle('is-active', x === h));
    $$('[data-look-item]', root).forEach((x) => x.classList.toggle('is-active', x.dataset.lookItem === h.dataset.hotspot));
  });

  /* ---------------- Predictive search ---------------- */
  const searchPanel = $('#SearchPanel');
  if (searchPanel) {
    const input = $('input[type="search"]', searchPanel);
    const results = $('[data-search-results]', searchPanel);
    const defaults = results ? results.innerHTML : '';
    let ctrl;
    on(input, 'input', debounce(() => {
      const q = input.value.trim();
      if (!q) { results.innerHTML = defaults; return; }
      if (ctrl) ctrl.abort();
      ctrl = new AbortController();
      fetch(`${R.routes.search}?q=${encodeURIComponent(q)}&resources[type]=product,collection,page,article&resources[limit]=10&section_id=predictive-search`, { signal: ctrl.signal })
        .then((r) => r.text())
        .then((html) => {
          const inner = $('[data-predictive]', parseHTML(html));
          if (inner) { results.innerHTML = inner.outerHTML; initReveal(results); }
        })
        .catch(() => {});
    }, 280));
  }

  /* ---------------- Collection filters (AJAX) ---------------- */
  const coll = $('[data-collection]');
  if (coll) {
    const sectionId = coll.dataset.sectionId;
    const load = (url, push = true) => {
      const grid = $('[data-product-grid]');
      if (grid) grid.style.opacity = '.4';
      const u = new URL(url, window.location.origin);
      u.searchParams.set('section_id', sectionId);
      fetch(u.toString())
        .then((r) => r.text())
        .then((html) => {
          const doc = parseHTML(html);
          ['[data-product-grid-wrap]', '[data-facets-inner]', '[data-active-filters]', '[data-toolbar-count]'].forEach((sel) => {
            const fresh = $(sel, doc), cur = $(sel);
            if (fresh && cur) cur.replaceWith(fresh);
          });
          if (push) { u.searchParams.delete('section_id'); window.history.pushState({}, '', u.toString()); }
          initReveal($('[data-product-grid-wrap]'));
          initInfinite();
          applyGridCols();
        })
        .catch(() => { window.location.href = url; });
    };
    const formUrl = (form) => {
      const params = new URLSearchParams(new FormData(form));
      for (const [k, v] of Array.from(params.entries())) if (v === '') params.delete(k);
      return `${window.location.pathname}?${params.toString()}`;
    };
    on(document, 'change', (e) => {
      const form = e.target.closest('[data-facets-form]');
      if (form && !e.target.matches('[data-price-input]')) load(formUrl(form));
      if (e.target.matches('[data-sort]')) {
        const f = $('[data-facets-form]');
        const radio = f && $(`input[name="sort_by"][value="${CSS.escape(e.target.value)}"]`, f);
        if (radio) { radio.checked = true; load(formUrl(f)); }
        else { const u = new URL(window.location.href); u.searchParams.set('sort_by', e.target.value); load(u.toString()); }
      }
    });
    on(document, 'change', debounce((e) => {
      if (e.target.matches('[data-price-input]')) load(formUrl(e.target.closest('form')));
    }, 700));
    on(document, 'submit', (e) => {
      const form = e.target.closest('[data-facets-form]');
      if (form) { e.preventDefault(); load(formUrl(form)); closePanel(); }
    });
    on(document, 'click', (e) => {
      const a = e.target.closest('[data-filter-link]');
      if (a) { e.preventDefault(); load(a.href); }
    });
    on(window, 'popstate', () => load(window.location.href, false));

    // Grid columns switch
    const applyGridCols = () => {
      let cols = null;
      try { cols = localStorage.getItem('rizale-grid'); } catch (_) { /* storage unavailable */ }
      const grid = $('[data-product-grid]');
      if (cols && grid) grid.style.setProperty('--cols', cols);
      $$('[data-grid-cols]').forEach((b) => b.classList.toggle('is-active', b.dataset.gridCols === (cols || (grid && grid.dataset.defaultCols))));
    };
    on(document, 'click', (e) => {
      const b = e.target.closest('[data-grid-cols]');
      if (!b) return;
      try { localStorage.setItem('rizale-grid', b.dataset.gridCols); } catch (_) { /* ignore */ }
      applyGridCols();
    });
    applyGridCols();

    // Infinite scroll / load more
    function initInfinite() {
      const more = $('[data-load-more]');
      if (!more) return;
      const fetchMore = () => {
        if (more.dataset.loading) return;
        more.dataset.loading = '1';
        more.classList.add('is-loading');
        const u = new URL(more.dataset.next, window.location.origin);
        u.searchParams.set('section_id', sectionId);
        fetch(u.toString()).then((r) => r.text()).then((html) => {
          const doc = parseHTML(html);
          const items = $$('[data-product-grid] > *', doc);
          const grid = $('[data-product-grid]');
          items.forEach((it) => grid.appendChild(it));
          const freshMore = $('[data-load-more-wrap]', doc);
          const curMore = $('[data-load-more-wrap]');
          if (curMore) curMore.replaceWith(freshMore || document.createComment(''));
          initReveal(grid);
          initInfinite();
        });
      };
      on($('button', more), 'click', fetchMore);
      if (more.dataset.mode === 'infinite' && 'IntersectionObserver' in window) {
        const io = new IntersectionObserver(([en]) => { if (en.isIntersecting) { io.disconnect(); fetchMore(); } }, { rootMargin: '400px' });
        io.observe(more);
      }
    }
    initInfinite();
  }

  /* ---------------- Shopify theme editor ---------------- */
  document.addEventListener('shopify:section:load', (e) => {
    initReveal(e.target);
    $$('[data-product]', e.target).forEach((el) => new ProductForm(el));
  });
  document.addEventListener('shopify:block:select', (e) => {
    const slide = e.target.closest('.hero-slide');
    if (slide) {
      const root = slide.closest('[data-hero-slider]');
      $$('.hero-slide', root).forEach((s) => s.classList.toggle('is-active', s === slide));
    }
  });
})();
